import { useEffect, useRef } from "react";
import { HQ_MILESTONES, buildingById } from "@zend/content";
import type { HqUnlock } from "@zend/rules";
import { AssetIcon } from "../assets/Icon.js";
import { Icon } from "../ui/Icon.js";
import { Button } from "../ui/Button.js";
import { pick, t, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { useProgression } from "./progression-state.js";
import type { HqCelebrationEvent } from "./progression-api.js";
import "./progression.css";
import { useUnits } from "../units/state.js";

function unlockText(unlock: HqUnlock): string {
  if (unlock.kind === "building") return pick(buildingById(unlock.id).name);
  if (unlock.kind === "copies") return t("progression.copy", { name: pick(buildingById(unlock.id).name), n: unlock.count });
  if (unlock.kind === "zone") return t("progression.zone", { name: t(`zone.${unlock.id}`) });
  if (unlock.kind === "collect_all") return t("progression.collectAll");
  return t("progression.buildingCap", { n: unlock.level });
}

function Celebration({ event }: { event: HqCelebrationEvent }) {
  const tt = useT();
  const dialog = useRef<HTMLElement>(null);
  const busy = useProgression((s) => s.busy);
  const error = useProgression((s) => s.error);
  const acknowledge = useProgression((s) => s.acknowledge);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);
  const close = async (store = false) => { if (await acknowledge(event.id)) { if (store) navigate("/app/store/featured"); } };
  return <section ref={dialog} className="hq-celebration" role="dialog" aria-modal="true" aria-labelledby="hq-celebration-title" tabIndex={-1} data-testid="hq-celebration" onKeyDown={(e) => {
    if (e.key === "Escape") { e.preventDefault(); void close(); }
    if (e.key !== "Tab") return;
    const buttons = Array.from(dialog.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []);
    const first = buttons[0]; const last = buttons.at(-1);
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { e.preventDefault(); first?.focus(); }
  }}>
    <div className="hq-celebration-layout">
      <header className="hq-celebration-hero">
        <p className="hq-eyebrow">{tt("progression.chapter")}</p>
        <div className="hq-level-mark" aria-hidden="true"><span>{event.fromLevel}</span><Icon name="arrowRight" size={24} /><strong>{event.level}</strong></div>
        <h1 id="hq-celebration-title">{tt("progression.hqLevel", { n: event.level })}</h1>
        <p>{tt("progression.subtitle")}</p>
        <AssetIcon id="bld.command_nexus" size={300} className="hq-celebration-building" alt={pick(buildingById("bld.command_nexus").name)} />
        <div className="hq-celebration-power"><span>{tt("progression.total")}</span><strong>{event.powerAfter.toLocaleString()}</strong><b data-testid="hq-power-delta">+{(event.powerAfter - event.powerBefore).toLocaleString()}</b></div>
      </header>
      <div className="hq-celebration-content">
        <div className="hq-unlocks"><h2>{tt("progression.unlocked")}</h2><ul>
          {event.unlocks.map((unlock) => <li key={`${unlock.kind}:${unlock.id}:${unlock.level}`} data-unlock={unlock.id}>
            {unlock.kind === "building" || unlock.kind === "copies" ? <AssetIcon id={unlock.id} size={44} /> : <span className="hq-unlock-symbol"><Icon name={unlock.kind === "zone" ? "expand" : unlock.kind === "collect_all" ? "check" : "arrowUp"} size={22} /></span>}
            <span>{unlockText(unlock)}</span>
          </li>)}
        </ul></div>
        {event.rewards.length > 0 && <div className="hq-milestone-rewards" data-testid="hq-milestone-rewards"><h2>{tt("progression.earned")}</h2>{event.rewards.map((reward) => <div key={reward.level} className="hq-milestone-reward">
          <AssetIcon id="itm.base_skin_fragment" size={50} />
          <div><strong>{pick(HQ_MILESTONES.find((m) => m.level === reward.level)!.title)}</strong><p>{tt("progression.titleAndFragment", { n: reward.fragments })}</p></div>
        </div>)}</div>}
        <footer className="hq-celebration-actions">
          {error && <p role="alert">{error}</p>}
          <Button variant="primary" loading={busy} onClick={() => void close()} data-testid="hq-celebration-continue">{tt("progression.continue")}<Icon name="arrowRight" size={18} /></Button>
          <Button variant="ghost" disabled={busy} onClick={() => void close(true)}>{tt("progression.offers")}</Button>
        </footer>
      </div>
    </div>
  </section>;
}

/** Deferred during a fight/reveal: the durable event appears on the next visit to the base. */
export function HqCelebration({ active }: { active: boolean }) {
  const event = useProgression((s) => s.celebrations[0]);
  const occupied = useUnits((s) => !!s.reveal || !!s.result);
  return active && !occupied && event ? <Celebration key={event.id} event={event} /> : null;
}
